import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Activity, RefreshCcw, Loader2, Search, Wrench, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFeedsAnalyzer() {
  const { authFetch } = useAdmin();
  const [data, setData] = useState({ items: [], reference_feeds: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [discoverUrl, setDiscoverUrl] = useState('');
  const [discoverResult, setDiscoverResult] = useState(null);
  const [discBusy, setDiscBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/feeds/analyze`);
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function discover() {
    if (!discoverUrl.trim()) return;
    setDiscBusy(true); setDiscoverResult(null);
    try {
      const r = await authFetch(`${API}/admin/feeds/discover`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: discoverUrl }),
      });
      if (r.ok) setDiscoverResult(await r.json());
    } finally { setDiscBusy(false); }
  }

  async function applyBestPractice() {
    if (!window.confirm('Primijeniti baseline postavke (AI obrada + skip_terms) na sve feedove osim referentnih? Ova akcija se može ručno poništiti u /admin/rss.')) return;
    setApplyBusy(true);
    try {
      const r = await authFetch(`${API}/admin/feeds/apply-best-practice`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enable_ai: true, add_skip_terms: true }),
      });
      const d = await r.json();
      toast.success(`Primijenjeno na ${d.updated || 0} feedova (od ${d.candidates || 0} kandidata).`);
      load();
    } finally { setApplyBusy(false); }
  }

  return (
    <div className="p-4 md:p-6" data-testid="admin-feeds-analyzer">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <Activity className="text-emerald-500" size={28} />
          <div>
            <h1 className="text-2xl font-bold">Analiza RSS izvora</h1>
            <p className="text-sm text-zinc-400">Usporedba sa najboljim feedovima (Čakovec.hr i Međimurska županija) + automatsko prilagođavanje.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="reload-analyze-btn"
                  className="px-3 py-2 rounded text-xs font-bold border border-zinc-700 hover:border-emerald-500/60 disabled:opacity-50">
            <RefreshCcw size={12} className="inline mr-1"/> Osvježi
          </button>
          <button onClick={applyBestPractice} disabled={applyBusy} data-testid="apply-best-practice-btn"
                  className="px-3 py-2 rounded text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50">
            {applyBusy ? <Loader2 size={12} className="animate-spin inline mr-1"/> : <Wrench size={12} className="inline mr-1"/>}
            Primijeni baseline
          </button>
        </div>
      </div>

      {/* Discover RSS */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 mb-5">
        <h2 className="text-sm font-bold mb-2 flex items-center gap-2"><Search size={14}/> Otkrivanje RSS URL-a</h2>
        <p className="text-xs text-zinc-500 mb-3">Unesi homepage adresu (npr. https://www.varazdin.hr/novosti/) i sustav će pronaći ispravan RSS endpoint.</p>
        <div className="flex gap-2">
          <input value={discoverUrl} onChange={e => setDiscoverUrl(e.target.value)}
                 placeholder="https://primjer.hr/" data-testid="discover-url-input"
                 className="flex-1 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm"/>
          <button onClick={discover} disabled={discBusy} data-testid="discover-btn"
                  className="px-4 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50">
            {discBusy ? <Loader2 size={14} className="animate-spin"/> : 'Otkrij'}
          </button>
        </div>
        {discoverResult && (
          <div className="mt-3 text-sm" data-testid="discover-result">
            {(discoverResult.found || []).length === 0 ? (
              <p className="text-rose-400">Nije pronađen RSS endpoint za tu adresu.</p>
            ) : (
              <p className="text-emerald-400">
                Pronađen RSS:{' '}
                <a href={discoverResult.found[0].url} target="_blank" rel="noopener noreferrer"
                   className="underline">{discoverResult.found[0].url}</a>
              </p>
            )}
          </div>
        )}
      </div>

      {loading ? <Loader2 className="animate-spin text-zinc-500"/> : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-900 text-xs text-zinc-400 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Feed</th>
                <th className="px-3 py-2 text-center">Health</th>
                <th className="px-3 py-2 text-center">Članci</th>
                <th className="px-3 py-2 text-center">AI</th>
                <th className="px-3 py-2 text-left">Preporuke</th>
              </tr>
            </thead>
            <tbody>
              {(data.items || []).map(it => (
                <tr key={it.id} data-testid={`feed-row-${it.id}`}
                    className={`border-t border-zinc-800 ${it.is_reference ? 'bg-emerald-500/5' : ''}`}>
                  <td className="px-3 py-2">
                    <p className="font-bold flex items-center gap-1">
                      {it.name}
                      {it.is_reference && <span className="text-[9px] uppercase tracking-widest bg-emerald-700 text-white px-1.5 py-0.5 rounded">REF</span>}
                    </p>
                    <a href={it.url} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-zinc-500 hover:text-emerald-400 inline-flex items-center gap-1">
                      <ExternalLink size={9}/> {it.host}
                    </a>
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-sm font-bold ${it.health_score >= 80 ? 'text-emerald-400' : it.health_score >= 50 ? 'text-amber-400' : 'text-rose-400'}`}>
                      {it.health_score}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center text-zinc-300">{it.articles_imported}</td>
                  <td className="px-3 py-2 text-center">
                    {it.ai_processing ? <span className="text-emerald-400">✓</span> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {(it.recommendations || []).length === 0 ? (
                      <span className="text-emerald-400 text-xs">U redu</span>
                    ) : (
                      <ul className="text-[11px] text-amber-300 list-disc pl-4">
                        {(it.recommendations || []).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
